import { supabase } from './supabase'
import { ENV } from './env'

async function authToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession()
  return data.session?.access_token ?? null
}

// A session from before TOTP enrollment (second device/tab) has a stale cached user with no
// factors — the server 401s mfa_required but the local assurance check can't see it. Refreshing
// the session pulls fresh user.factors, fires onAuthStateChange, and the MFA gate takes over.
let mfaRefresh: Promise<unknown> | null = null
function healMfaSession() {
  mfaRefresh ??= supabase.auth.refreshSession().finally(() => {
    mfaRefresh = null
  })
}

/**
 * Drop the service worker's copies of API answers (it keeps GETs for offline use). Called on every sign-out, so the
 * next person on a shared phone or tablet can never be shown the last one's data.
 */
export async function forgetCachedApi(): Promise<void> {
  try {
    if (typeof caches !== 'undefined') await caches.delete('api')
  } catch {
    /* no cache storage here */
  }
}

/** The login page reads this to say why the person was signed out (lib/auth, pages/Login). */
export const SIGNED_OUT_NOTE_KEY = 'reptree-signed-out-note'

/**
 * A session the server no longer knows (ended elsewhere: a passkey set up on another device, a password change,
 * support's reset) still looks valid here until its token runs out, so every page would fail for up to an hour.
 * Refresh it once: if that works, the fresh session carries the account's current second steps and the gate or
 * the app follows; if the session is gone for good, sign out on THIS device only and let the sign-in page say why.
 */
let resyncing: Promise<unknown> | null = null
/** The access token last resynced: a fresh token that still gets 401 is left alone (no refresh loop). */
let resyncedToken: string | null = null
/** Supabase answers that mean the session is truly gone (anything else, like a network fault, is not a sign-out). */
const GONE = new Set(['refresh_token_not_found', 'refresh_token_already_used', 'session_not_found', 'session_expired', 'user_not_found', 'user_banned'])
export function resyncSession(): Promise<unknown> {
  resyncing ??= (async () => {
    const { data } = await supabase.auth.getSession()
    const token = data.session?.access_token
    if (!token || token === resyncedToken) return
    resyncedToken = token
    const { error } = await supabase.auth.refreshSession()
    if (!error) return
    const e = error as { name?: string; code?: string }
    if (e.name !== 'AuthSessionMissingError' && !GONE.has(e.code ?? '')) return
    try {
      sessionStorage.setItem(SIGNED_OUT_NOTE_KEY, '1')
    } catch {
      /* private mode: the note is only a courtesy */
    }
    await supabase.auth.signOut({ scope: 'local' }).catch(() => undefined)
    await forgetCachedApi()
  })().finally(() => {
    resyncing = null
  })
  return resyncing
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

/**
 * Cool-down a 429 asks us to wait, in ms, from the standard rate-limit headers (`Retry-After`, or the
 * draft `RateLimit-Reset`, both in seconds). Returns null when there's no usable hint. Used only to
 * ride out a MOMENTARY throttle: a single portal view fans out into many parallel GETs, so a burst
 * can clip the window's tail; when the server says it reopens in a beat, we wait rather than surfacing
 * "too_many_requests" on a panel the user never actually hammered.
 */
function retryAfterMs(res: Response): number | null {
  for (const h of ['retry-after', 'ratelimit-reset']) {
    const raw = res.headers.get(h)
    if (raw == null) continue
    const secs = Number(raw)
    if (Number.isFinite(secs)) return Math.max(0, secs * 1000)
  }
  return null
}

/**
 * ONE authenticated request against the RepTree API, handed back unparsed.
 *
 * Split out of `api()` so a caller that owns its own response handling still gets the base URL and the
 * Supabase bearer from exactly one place. The platform-kit verticals are the reason it exists: their client
 * transports take a `fetch`-shaped function and parse the body themselves (see pages/sales/client.ts), and
 * the alternative was a second copy of the header logic that would drift the first time auth changed.
 *
 * A momentary 429 on an IDEMPOTENT read is retried a bounded number of times, but only when the server's
 * reset hint is near (never a blind spin, never a replayed write). This is the one place every request
 * flows through, so the whole app inherits the recovery instead of each panel growing its own.
 *
 * Prefer `api()` for RepTree's own routes: it is this plus the error convention and the 204 case.
 */
export async function apiFetch(path: string, opts: RequestInit = {}): Promise<Response> {
  const token = await authToken()
  const method = (opts.method ?? 'GET').toUpperCase()
  const idempotent = method === 'GET' || method === 'HEAD'
  const init: RequestInit = {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers ?? {}),
    },
  }
  const url = `${ENV.API_BASE}/api${path}`
  let res = await fetch(url, init)
  for (let attempt = 0; idempotent && res.status === 429 && attempt < 2; attempt++) {
    const waitMs = retryAfterMs(res)
    // Only ride out a SHORT throttle: a far-off reset means the window is genuinely spent, so fail
    // fast (surface the error) exactly as before rather than freezing the UI for minutes.
    if (waitMs == null || waitMs > 5000) break
    await sleep(waitMs + 100)
    res = await fetch(url, init)
  }
  return res
}

/**
 * What a failed `api()` call throws: an Error with the server's message (so every `(e as Error).message` reads as
 * before), plus the HTTP status and the structured `details` a route may add (for example the stale keys of a 409).
 */
export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly details?: unknown) {
    super(message)
  }
}

/** True when `e` is a failed API call that answered this status. */
export const isApiStatus = (e: unknown, status: number): e is ApiError => e instanceof ApiError && e.status === status

/** Typed fetch against the RepTree API with the Supabase bearer token attached. */
export async function api<T = unknown>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await apiFetch(path, opts)
  if (!res.ok) {
    let message = res.statusText
    let details: unknown
    try {
      // RepTree's own routes say { error } (with optional { details }); the platform-kit verticals say { message }.
      const body = (await res.json()) as { error?: string; message?: string; details?: unknown }
      const m = body?.error ?? body?.message
      if (m) message = m
      details = body?.details
    } catch {
      /* ignore */
    }
    if (res.status === 401 && message === 'mfa_required') healMfaSession()
    else if (res.status === 401 && message === 'unauthorized') void resyncSession()
    throw new ApiError(message, res.status, details)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}
